import { format, parse } from 'date-fns';

export const BUSINESS_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const BUSINESS_DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const DEFAULT_OPEN = () => parse('9:00 AM', 'h:mm aa', new Date());
const DEFAULT_CLOSE = () => parse('5:00 PM', 'h:mm aa', new Date());

const defaultDayHours = (dayKey, defaultOpen, defaultClose) => ({
  open: defaultOpen,
  close: defaultClose,
  isOpen: dayKey !== 'saturday' && dayKey !== 'sunday',
  hasBreak: false,
  breakStart: null,
  breakEnd: null,
});

export const buildDefaultBusinessHoursState = () => {
  const defaultOpen = DEFAULT_OPEN();
  const defaultClose = DEFAULT_CLOSE();
  return BUSINESS_DAYS.reduce((acc, dayKey) => {
    acc[dayKey] = defaultDayHours(dayKey, defaultOpen, defaultClose);
    return acc;
  }, {});
};

export const parseStoredBusinessHours = (storedHours = {}) => {
  const defaultOpen = DEFAULT_OPEN();
  const defaultClose = DEFAULT_CLOSE();
  const parsed = {};

  BUSINESS_DAYS.forEach((dayKey) => {
    const existingHours = storedHours?.[dayKey] || {};
    parsed[dayKey] = {
      open: existingHours.open
        ? parse(existingHours.open, 'h:mm aa', new Date())
        : defaultOpen,
      close: existingHours.close
        ? parse(existingHours.close, 'h:mm aa', new Date())
        : defaultClose,
      isOpen: existingHours.isOpen ?? (dayKey !== 'saturday' && dayKey !== 'sunday'),
      hasBreak: existingHours.hasBreak ?? false,
      breakStart: existingHours.breakStart
        ? parse(existingHours.breakStart, 'h:mm aa', new Date())
        : null,
      breakEnd: existingHours.breakEnd
        ? parse(existingHours.breakEnd, 'h:mm aa', new Date())
        : null,
    };
  });

  return parsed;
};

export const formatBusinessHoursForStorage = (hoursState = {}) => {
  const formatted = {};

  BUSINESS_DAYS.forEach((dayKey) => {
    const hours = hoursState[dayKey] || {};
    formatted[dayKey] = {
      open:
        hours.open instanceof Date && !Number.isNaN(hours.open.getTime())
          ? format(hours.open, 'h:mm aa').toLowerCase()
          : '9:00 am',
      close:
        hours.close instanceof Date && !Number.isNaN(hours.close.getTime())
          ? format(hours.close, 'h:mm aa').toLowerCase()
          : '5:00 pm',
      isOpen: hours.isOpen ?? true,
      hasBreak: hours.hasBreak ?? false,
      breakStart:
        hours.breakStart instanceof Date && !Number.isNaN(hours.breakStart.getTime())
          ? format(hours.breakStart, 'h:mm aa').toLowerCase()
          : null,
      breakEnd:
        hours.breakEnd instanceof Date && !Number.isNaN(hours.breakEnd.getTime())
          ? format(hours.breakEnd, 'h:mm aa').toLowerCase()
          : null,
    };
  });

  return formatted;
};

export const getLocationHoursSummary = (location, companyData = {}) => {
  if (location?.isDefault || !location?.businessHours) {
    return 'Uses company business hours';
  }
  return 'Custom business hours';
};

export const resolveLocationOpenOnHolidays = (location, companyData = {}) => {
  if (location?.isDefault || !location?.businessHours) {
    return companyData?.settings?.openOnHolidays ?? false;
  }
  if (typeof location.openOnHolidays === 'boolean') {
    return location.openOnHolidays;
  }
  return companyData?.settings?.openOnHolidays ?? false;
};
